import z from 'zod';
const pmaDerDeclarationSchema = z.looseObject({
    scope: z.enum(['pma', 'der', 'non_oem_mod']),
    finding: z.enum(['declared_absent', 'present', 'deferred', 'unclear']),
    /** Verbatim sentence the verdict was read from — the grounding evidence, never empty. */
    quote: z.string(),
    /** Every source page the declaration was seen on (a multi-page list repeats its header statement). */
    pageIds: z.array(z.string()),
});
const pmaDerPartSchema = z.looseObject({
    partNumber: z.string().optional(),
    serialNumber: z.string().optional(),
    description: z.string().optional(),
    documentType: z.enum(['pma_part', 'der_repair']).optional(),
    approvalTypeRaw: z.string().optional(),
    approvalNumber: z.string().optional(),
    issuingAuthority: z.string().optional(),
    applicability: z.string().optional(),
    date: z.string().optional(),
    quantity: z.number().optional(),
    remarks: z.string().optional(),
    pageIds: z.array(z.string()),
});
export const pmaDerEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('pma-der-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `document-header-ready`
    // rather than the looser `project-progress` / `project-data-ready` spelling.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    /**
     * EVERY PMA/DER entity of the project, not a single winner (DR-3128). A PMA/DER
     * statement asserts a compliance position at one moment, so a project holds
     * several and the consumer reads them as a timeline. `declarations` and `parts`
     * are always present, possibly empty: a clean negative statement ("no PMA parts
     * installed") legitimately carries declarations and zero parts, and that is the
     * most common shape in the corpus.
     */
    pmaDerEvents: z.array(z.looseObject({
        documentId: z.string(),
        reconstructedDocumentId: z.string(),
        pageIds: z.array(z.string()),
        statementDate: z.string().optional(),
        /** First/last day of the period the statement covers, YYYY-MM-DD (DR-4192). Absent when the page printed no window. */
        coveragePeriodStart: z.string().optional(),
        coveragePeriodEnd: z.string().optional(),
        /**
         * DR-5210: the statement says the covered period starts when the engine was new ('from New',
         * 'since new', 'since manufacture'); `coveragePeriodStart` is then absent. Only ever `true` or
         * absent. Brain emits it on NIS rows now; PMA/DER rows emit it under DR-5211.
         */
        coverageSinceNew: z.boolean().optional(),
        /** Company that signed the statement. Absent when pages disagree or none printed a signer (DR-4294). */
        issuerName: z.string().optional(),
        /** `operator` or `camo_on_behalf`. Absent for a shop/lessor/unclear signer, and when pages disagree (DR-4294). */
        issuerRole: z.enum(['operator', 'camo_on_behalf']).optional(),
        /**
         * Engine serials the letter stated, as printed. Optional so a consumer upgrading ahead of
         * Brain still accepts pre-DR-4294 envelopes; Brain always emits the array, empty when none.
         */
        esns: z.array(z.string()).optional(),
        // DR-6009: what the letter's certifying sentence is about. `aircraft` means the window is the
        // AIRCRAFT's period and the item counts for an engine only for its stays on that aircraft;
        // absent means engine. The registration and MSN are sent only on an `aircraft` item,
        // verbatim. Only NIS-letter items carry the field.
        statementSubject: z.enum(['aircraft', 'engine']).optional(),
        aircraftRegistration: z.string().optional(),
        aircraftMsn: z.string().optional(),
        declarations: z.array(pmaDerDeclarationSchema),
        parts: z.array(pmaDerPartSchema),
    })),
});
//# sourceMappingURL=pma-der-events-ready.webhook.js.map